import React, { useState } from 'react';
import { auth } from '../Firebase';
import { signInWithEmailAndPassword, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowRight } from 'lucide-react';
import loginImg from '../assets/admin-login.png';
import finalLogo from '../assets/logo/final-logo.png';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        localStorage.setItem('admin_last_active', Date.now().toString());
        localStorage.setItem('admin_last_active_write', Date.now().toString());
        navigate('/admin/dashboard');
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      localStorage.setItem('admin_last_active', Date.now().toString());
      localStorage.setItem('admin_last_active_write', Date.now().toString());
      Swal.fire({
        icon: 'success',
        title: 'Welcome Back!',
        text: 'Access granted to Sangram Admin Panel',
        timer: 2000,
        showConfirmButton: false,
        background: '#fff',
        color: '#0D47A1'
      });
      navigate('/admin/dashboard');
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Authentication Failed',
        text: 'The credentials provided do not match our records.',
        confirmButtonColor: '#0D47A1'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated Background Elements */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{ duration: 20, repeat: Infinity }}
        className="absolute -top-[10%] -right-[10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-[120px]" 
      />
      <motion.div 
        animate={{ 
          scale: [1, 1.3, 1],
          rotate: [0, -90, 0],
        }}
        transition={{ duration: 25, repeat: Infinity }}
        className="absolute -bottom-[10%] -left-[10%] w-[50%] h-[50%] bg-secondary/10 rounded-full blur-[120px]" 
      />

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="max-w-5xl w-full bg-white rounded-[3rem] shadow-2xl shadow-primary/10 flex overflow-hidden border border-white relative z-10"
      >
        {/* Left Side: Visuals */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img 
            src={loginImg} 
            alt="Bank Interior" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D47A1]/95 via-[#1565C0]/85 to-transparent" />
          <div className="absolute inset-0 p-16 flex flex-col items-center justify-center text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex flex-col items-center"
            >
              <img 
                src={finalLogo} 
                alt="Sangram Logo" 
                className="w-32 h-32 md:w-36 md:h-36 object-contain rounded-full bg-white p-2 shadow-2xl border-2 border-white/50 mb-6 hover:scale-105 transition-transform duration-300"
              />
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-[0.15em] uppercase mb-2">
                SANGRAM
              </h2>
              <span className="text-sm font-black text-[#F59E0B] tracking-[0.18em] uppercase block">
                Patsanstha
              </span>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="w-full lg:w-1/2 p-12 lg:p-20 flex flex-col justify-center">
          <div className="mb-12">
            <div className="lg:hidden mb-6 flex justify-start">
              <img 
                src={finalLogo} 
                alt="Sangram Logo" 
                className="w-16 h-16 object-contain rounded-full bg-white p-0.5 shadow-md"
              />
            </div>
            <h1 className="text-4xl font-black text-primary-dark mb-3">Admin Login</h1>
            <p className="text-gray-500 font-medium">Please enter your authorized credentials.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 tracking-wide uppercase">Email Identity</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-100 rounded-[1.5rem] focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white outline-none transition-all text-gray-700 font-medium"
                  placeholder="admin@sangram.com"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 tracking-wide uppercase">Security Key</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-12 pr-14 py-4 bg-gray-50 border border-gray-100 rounded-[1.5rem] focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white outline-none transition-all text-gray-700 font-medium"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-400 hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </motion.div>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-5 rounded-[1.5rem] shadow-2xl shadow-primary/30 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3 mt-4 overflow-hidden relative group"
            >
              <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              {loading ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  Validate & Access
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </motion.button>
          </form>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-12 pt-8 border-t border-gray-50 text-center"
          >
            <p className="text-xs text-gray-400 uppercase tracking-widest font-black flex items-center justify-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              End-to-End Encrypted Session
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 text-gray-400 text-sm font-medium"
      >
        © {new Date().getFullYear()} Sangram Patsanstha Maryadit • Enterprise Systems
      </motion.p>
    </div>
  );
};

export default AdminLogin;

